import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

/** Run parameters a legacy simulation record may have been saved without. */
export type RerunMissingField =
  | "startTime"
  | "duration"
  | "droughtFactor"
  | "curing"
  | "ignitionRegime"
  | "weather";

/**
 * Constant conditions expanded to every hour of the run when the original
 * hourly weather is no longer on the record.
 */
export interface ConstantWeather {
  temperature: number;
  wind_speed: number;
  wind_direction: number;
  relative_humidity: number;
}

/** User-supplied values for the missing fields; only missing keys are set. */
export interface RerunMissingValues {
  startTime?: string;
  duration?: number;
  droughtFactor?: number;
  curing?: number;
  ignitionRegimeId?: number;
  constantWeather?: ConstantWeather;
}

interface Props {
  open: boolean;
  simulationName: string;
  /**
   * The project's satellite curing reading, used to seed the field instead of
   * a fixed number. A measurement is a far better starting point than 60.
   */
  suggestedCuring?: number | null;
  missingFields: RerunMissingField[];
  ignitionRegimes: Array<{ id: number; name: string }>;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (values: RerunMissingValues) => void;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
    "&.Mui-focused fieldset": { borderColor: "#ff6b35" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-focused": { color: "#ff6b35" },
  },
  "& .MuiSvgIcon-root": { color: "rgba(255, 255, 255, 0.7)" },
  // Keep the native datetime picker icon visible on the dark background
  "& input::-webkit-calendar-picker-indicator": { filter: "invert(1)" },
};

const helperSx = { sx: { color: "rgba(255, 255, 255, 0.4)" } };

const darkMenuProps = {
  MenuProps: { PaperProps: { sx: { bgcolor: "#1a1d2e", color: "#fff" } } },
};

const parseNum = (value: string): number | null => {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const inRange = (value: string, min: number, max: number): boolean => {
  const n = parseNum(value);
  return n !== null && n >= min && n <= max;
};

/**
 * Collects the run parameters a legacy simulation record is missing before it
 * can be rerun. Only the missing fields are shown; everything else is reused
 * verbatim from the saved record.
 */
const RerunMissingDataModal: React.FC<Props> = ({
  open,
  simulationName,
  missingFields,
  suggestedCuring,
  ignitionRegimes,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const needs = (field: RerunMissingField) => missingFields.includes(field);

  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("8");
  const [droughtFactor, setDroughtFactor] = useState("10");
  const [curing, setCuring] = useState("60");
  const [regimeId, setRegimeId] = useState<number | "">("");
  const [temperature, setTemperature] = useState("30");
  const [windSpeed, setWindSpeed] = useState("20");
  const [windDirection, setWindDirection] = useState("0");
  const [humidity, setHumidity] = useState("30");

  // Re-seed defaults each time the dialog opens for a new simulation
  useEffect(() => {
    if (!open) return;
    setStartTime(dayjs().startOf("hour").format("YYYY-MM-DDTHH:mm"));
    setDuration("8");
    setDroughtFactor("10");
    setCuring(
      suggestedCuring !== null && suggestedCuring !== undefined
        ? String(Math.round(suggestedCuring))
        : "60",
    );
    setRegimeId("");
    setTemperature("30");
    setWindSpeed("20");
    setWindDirection("0");
    setHumidity("30");
  }, [open, suggestedCuring]);

  const valid =
    (!needs("startTime") || dayjs(startTime).isValid()) &&
    (!needs("duration") || inRange(duration, 8, 48)) &&
    (!needs("droughtFactor") || inRange(droughtFactor, 0, 10)) &&
    (!needs("curing") || inRange(curing, 0, 100)) &&
    (!needs("ignitionRegime") || regimeId !== "") &&
    (!needs("weather") ||
      (inRange(temperature, -20, 60) &&
        inRange(windSpeed, 0, 200) &&
        inRange(windDirection, 0, 360) &&
        inRange(humidity, 0, 100)));

  const handleConfirm = () => {
    if (!valid) return;
    const values: RerunMissingValues = {};
    if (needs("startTime")) values.startTime = dayjs(startTime).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
    if (needs("duration")) values.duration = parseNum(duration)!;
    if (needs("droughtFactor")) values.droughtFactor = parseNum(droughtFactor)!;
    if (needs("curing")) values.curing = parseNum(curing)!;
    if (needs("ignitionRegime")) values.ignitionRegimeId = regimeId as number;
    if (needs("weather")) {
      values.constantWeather = {
        temperature: parseNum(temperature)!,
        wind_speed: parseNum(windSpeed)!,
        wind_direction: parseNum(windDirection)!,
        relative_humidity: parseNum(humidity)!,
      };
    }
    onConfirm(values);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1d2e", color: "#fff" } }}
    >
      <DialogTitle sx={{ color: "#fff" }}>Missing Simulation Details</DialogTitle>
      <DialogContent dividers sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 2 }}>
          "{simulationName}" was saved without some of the details needed to rerun it. Please
          specify the missing information below.
        </Typography>
        <Stack spacing={2}>
          {needs("startTime") && (
            <TextField
              label="Start time"
              type="datetime-local"
              size="small"
              fullWidth
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          )}
          {needs("duration") && (
            <TextField
              label="Duration (hours)"
              type="number"
              size="small"
              fullWidth
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 8, max: 48 }}
              error={!inRange(duration, 8, 48)}
              helperText="8–48 hours"
              FormHelperTextProps={helperSx}
              sx={inputSx}
            />
          )}
          {needs("droughtFactor") && (
            <TextField
              label="Drought factor"
              type="number"
              size="small"
              fullWidth
              value={droughtFactor}
              onChange={(e) => setDroughtFactor(e.target.value)}
              inputProps={{ min: 0, max: 10, step: 0.5 }}
              error={!inRange(droughtFactor, 0, 10)}
              helperText="0–10"
              FormHelperTextProps={helperSx}
              sx={inputSx}
            />
          )}
          {needs("curing") && (
            <TextField
              label="Curing (%)"
              type="number"
              size="small"
              fullWidth
              value={curing}
              onChange={(e) => setCuring(e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              error={!inRange(curing, 0, 100)}
              helperText="0–100%"
              FormHelperTextProps={helperSx}
              sx={inputSx}
            />
          )}
          {needs("ignitionRegime") && (
            <TextField
              label="Ignition regime"
              select
              size="small"
              fullWidth
              value={regimeId}
              onChange={(e) => setRegimeId(Number(e.target.value))}
              SelectProps={darkMenuProps}
              sx={inputSx}
            >
              {ignitionRegimes.map((regime) => (
                <MenuItem key={regime.id} value={regime.id}>
                  {regime.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {needs("weather") && (
            <>
              <Typography variant="subtitle2" sx={{ color: "rgba(255, 255, 255, 0.7)", mt: 1 }}>
                Weather
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", mt: "0 !important" }}>
                The original hourly weather is unavailable; these conditions will be applied to
                every hour of the run.
              </Typography>
              <TextField
                label="Temperature (°C)"
                type="number"
                size="small"
                fullWidth
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                inputProps={{ min: -20, max: 60 }}
                error={!inRange(temperature, -20, 60)}
                sx={inputSx}
              />
              <TextField
                label="Wind speed (km/h)"
                type="number"
                size="small"
                fullWidth
                value={windSpeed}
                onChange={(e) => setWindSpeed(e.target.value)}
                inputProps={{ min: 0, max: 200 }}
                error={!inRange(windSpeed, 0, 200)}
                sx={inputSx}
              />
              <TextField
                label="Wind direction (°)"
                type="number"
                size="small"
                fullWidth
                value={windDirection}
                onChange={(e) => setWindDirection(e.target.value)}
                inputProps={{ min: 0, max: 360 }}
                error={!inRange(windDirection, 0, 360)}
                helperText="0–360, degrees the wind blows from"
                FormHelperTextProps={helperSx}
                sx={inputSx}
              />
              <TextField
                label="Relative humidity (%)"
                type="number"
                size="small"
                fullWidth
                value={humidity}
                onChange={(e) => setHumidity(e.target.value)}
                inputProps={{ min: 0, max: 100 }}
                error={!inRange(humidity, 0, 100)}
                sx={inputSx}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting} sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!valid || submitting}
          sx={{ bgcolor: "#ff6b35", "&:hover": { bgcolor: "#ff7f50" } }}
        >
          {submitting ? "Starting…" : "Rerun"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RerunMissingDataModal;
